import Accordion from 'components/accordion/Accordion'
import Images from 'assets/images'
import React, { useEffect, useState } from 'react'
import SimpleHeader from 'components/header/SimpleHeader'
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';
import Spacer from 'components/Spacer/Spacer';
import { useApiQuery } from 'hooks/useApiQuery';
import { currentUserAction } from 'api/actions/UserActions';
import LoaderComponent from 'components/loader/LoaderComponent';
import classNames from 'classnames';
import { Icon } from 'stories/Icons';
import { getTicketsAction } from 'api/actions/SupportActions';
import moment from 'moment';
import useModal from 'hooks/useModal';
import CreateTicketModal from '../support/CreateTicketModal';
import useToastify from 'hooks/useToastify';
import { FaPlus } from "react-icons/fa6";

const PER_PAGE = 10;

function Support() {
    const history = useHistory();
    const { setShowModal } = useModal();
    const { showAlert } = useToastify();
    const { payload: userDetailsFromApi, loading: isFetchingUser } = useApiQuery(
        currentUserAction
      );
    const [pageNumber, setPageNumber] = useState(1);
    const { payload: ticketsFromApi, loading: isFetchingTickets, query: refreshTickets } = useApiQuery(
        getTicketsAction(pageNumber, PER_PAGE)
    );
    const commonQueries = [
        {
            id: 1,
            question: "How do I change my password?",
            answer: "Go to your <strong>profile</strong> in your account, click on the <strong>settings</strong> icon (the circle in the upper right-hand corner), then scroll down and select “<strong>Change Password</strong>.”<br/> If you don’t know your current password, please start a chat with support for assistance.",
        },
        {
            id: 2,
            question: "Issues obtaining my prescription? Not covered, prior authorization needed, etc.?",
            answer: "First, <strong>contact your pharmacy</strong> to find out what’s preventing the prescription from being filled.<br/> If the issue involves a <strong>prior authorization</strong> or requires action from the <strong>medical staff</strong>, please <strong>start a chat with support</strong>.",
        },
        {
            id: 3,
            question: "Where do I find my receipt?",
            answer: "Go to “<strong>My Visits</strong>” and select the <strong>visit number</strong> associated with your case. A <strong>receipt</strong> will be generated for you to review or download once your visit is completed.",
        },
        {
            id: 4,
            question: "Where can I review my evaluation and treatment plan?",
            answer: "Go to “<strong>My Visits</strong>” and select the <strong>visit number</strong> associated with your case to review your <strong>evaluation and treatment regimen</strong>.",
        },
        {
            id: 5,
            question: "Why was a payment charged before I received my evaluation?",
            answer: "Your card may show a <strong>temporary hold</strong>; <strong>however, your charge will be finalized once your visit has been evaluated.</strong>",
        },
        {
            id: 6,
            question: "How do I update my insurance?",
            answer: "<strong>Forgot to update before current visit:</strong> Please <strong>message the medical assistant</strong> in your visit chat to update your insurance.<br/><strong>Prior to submitting visit:</strong> If you’re updating insurance for <strong>future visits</strong>, you can do so under <strong>“Update Insurance”</strong> while submitting a new visit. ",
        },
        {
            id: 7,
            question: "How do I update my phone number?",
            answer: "Go to “<strong>My Profile</strong>”, select “<strong>Update Profile</strong>,” and edit the <strong>phone number field</strong>.",
        },
        {
            id: 8,
            question: "How do I update my pharmacy?",
            answer: "<ul style='list-style-type: disc; list-style-position: inside;'><li>To update your pharmacy for <strong>future visits</strong>, go to “<strong>My Profile</strong>”, select “<strong>Pharmacy</strong>,” and edit the information.</li><li>If you need to update your pharmacy for a <strong>current visit</strong> awaiting evaluation or for <strong>prescriptions already sent</strong>, please <strong>start a chat with support</strong> or <strong>message your medical assistant</strong> in the open visit chat.</li></ul>",
        },
        {
            id: 9,
            question: "How do I update my address?",
            answer: "Go to “<strong>My Profile</strong>”, select “<strong>Update Profile</strong>,” and edit the <strong>address field</strong>. *Your address needs to reflect the state that you reside in.*",
        },
        {
            id: 10,
            question: "How do I update my payment method?",
            answer: "Go to “<strong>My Profile</strong>”, select “<strong>Payments</strong>,” then choose “<strong>Select another way to pay</strong>” to add a new payment method.",
        },
        {
            id: 11,
            question: "I recently turned 18, why can't I submit a visit?",
            answer: "Once a child turns 18, they can no longer be seen under a <strong>parent's account.</strong><br/> Please <strong>start a chat with support</strong> for instructions on how to <strong>transfer my account</strong> to an adult account.",
        }
    ]

    useEffect(() => {
        refreshTickets(pageNumber).catch(() => {
            showAlert("Failed to fetch tickets. Please try again.", "error");
        })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pageNumber]);

    const hasMorePages = ticketsFromApi?.paginate?.current_page < Math.ceil(ticketsFromApi?.paginate?.total_entries / ticketsFromApi?.paginate?.per_page);

    const accordionData = commonQueries.map((query) => ({
        header: (props) => (
            <div className="flex items-center justify-between p-4">
                <h3 className="text-2xl font-medium">{`${query.id}. ${query.question}`}</h3>
                <img
                    src={Images.dropdown}
                    alt="chev_down"
                    className={`transition duration-500 ease-in-out w-8 h-8 transform ${
                        props.isActive ? "rotate-180" : "rotate-0"
                    }`}
                />
            </div>
        ),
        content: (
            <div className="p-4 flex flex-col items-start justify-start">
                <p className="text-themeBlack text-xl font-medium" dangerouslySetInnerHTML={{ __html: query.answer }}></p>
                {userDetailsFromApi?.eligible_to_create_support_ticket && <>
                    <Spacer height={20} />
                    <button
                        className={classNames(
                            "w-fit relative flex items-center justify-between space-x-2 px-8 py-4 focus:ring-2 focus:ring-secondaryBlue-500 text-secondaryBlue-500 border border-secondaryBlue-500 rounded-md md:py-3 space-x-2 text-xl font-bold hover:opacity-80 focus:outline-none focus:border-transparent disabled:opacity-60 disabled:cursor-not-allowed"
                        )}
                        onClick={() => {
                            setShowModal({
                                state: true,
                                children: (_, onClose) => (
                                    <CreateTicketModal
                                        onClose={onClose}
                                        onSuccess={() => {
                                            refreshTickets(pageNumber);
                                        }}
                                    />
                                ),
                            });
                        }}
                        disabled={userDetailsFromApi?.any_open_support_ticket|| isFetchingUser}
                        >
                            <Icon name={"message"}/>
                            <span className="text-xl font-bold">Contact Support</span>
                    </button>
                </>}
            </div>
        ),
        className: "border-b border-gray-200 hover:bg-gray-50",
    }))

    if (isFetchingUser || isFetchingTickets) {
        return <LoaderComponent showLoader={{ state: true, message: "Fetching your support" }} />;
    }

    const getCommonQueries = () => (
        <div className="w-full p-6 flex flex-col space-y-2 bg-white mx-12 my-8">
            <h3 className="text-3xl font-bold px-4">Frequently Asked Questions</h3>
            <Spacer height={10} />
            <Accordion 
                data={accordionData}
                containerClassName="bg-white rounded-lg shadow-sm"
                accordionContainerClassName=""
            />
        </div> 
    )

    const getPageNumbers = () => {
        return Array(Math.ceil(ticketsFromApi?.paginate?.total_entries / PER_PAGE)).fill(0).map((_, index) => (
            <span key={index} className={classNames("transition duration-300 ease-in-out text-themeBlack text-lg font-medium", pageNumber === index + 1 ? "!text-white bg-orange rounded-full w-10 h-10 flex items-center justify-center cursor-pointer text-xl" : "text-themeBlack cursor-pointer")} onClick={() => {
                setPageNumber(index + 1)
            }}>{index + 1}</span>
        ))
    }

    return (
        <div className="w-full bg-backgroundWhite h-fit">
            <SimpleHeader
                title="Support"
                className="mr-2 w-full"
            />
            <Spacer height={20} />
            <div className="w-full px-8 flex flex-col space-y-2 pb-72 md:pb-48">
                {userDetailsFromApi?.eligible_to_create_support_ticket ? 
                <div className="w-full flex items-center justify-start flex-col">
                    {ticketsFromApi?.support_tickets?.length > 0 && <div className="w-full p-4 flex flex-col space-y-2 bg-white mx-12 my-8">
                        <h3 className="text-3xl font-bold px-4">Your Support Tickets</h3>
                        <Spacer height={10} />
                        <div className="flex flex-col space-y-4 p-2">
                            {
                                ticketsFromApi?.support_tickets?.map((ticket) =>(
                                    <div key={ticket?.id} className="w-full p-4 bg-white rounded-lg border flex items-center justify-between cursor-pointer" onClick={() => history.push(`/support/query/${ticket?.id}`)}>
                                        <div className="flex flex-col items-start justify-center">
                                            <h3 className="text-2xl font-medium flex items-center justify-start gap-2">
                                                <span>{ticket?.subject}</span>
                                                {ticket?.unread_ma_messages > 0 && <span className='rounded-full w-6 h-6 text-sm flex items-center justify-center bg-errorRed text-white'>{ticket?.unread_ma_messages}</span>}
                                            </h3>
                                            <Spacer height={10} />
                                            <p className="text-themeBlack text-base font-medium">{moment(new Date()).diff(moment(ticket?.created_at), "days") > 0 ? moment(ticket?.created_at).format("DD/MM/YYYY") : moment(ticket?.created_at).format("h:mm A")}</p>
                                        </div>
                                        <div className="flex items-end md:items-center justify-center gap-2 flex-col-reverse md:flex-row">
                                            {ticket?.status === "open" && ticket?.closed_requested && <p className="text-themeBlack text-base font-medium">(Closure Requested)</p>}
                                            <p className={classNames(`text-themeBlack text-base font-bold rounded-3xl px-2 py-1 capitalize`, ticket?.status === "open" ? "text-white bg-orange" : "text-white bg-successGreen")}>{ticket?.status === "closed" ? "Completed" : "open"}</p>
                                        </div>
                                    </div>
                                ))
                            }
                        </div>
                        {ticketsFromApi?.paginate?.total_entries > PER_PAGE && <div className="flex items-center justify-center space-x-4">
                            <button onClick={() => {
                                if(pageNumber === 1) {
                                    return;
                                }
                                if(pageNumber > 1) {
                                    setPageNumber(pageNumber - 1)
                                }
                            }} disabled={pageNumber === 1} 
                                type="button"
                                className="w-fit"
                            ><Icon name="arrow-right" className="w-10 h-10 rotate-180" /></button>
                            {getPageNumbers()}
                            <button onClick={() => {
                                if(hasMorePages) {
                                    setPageNumber(pageNumber + 1)
                                }
                            }} disabled={!hasMorePages} className="w-fit" ><Icon name="arrow-right" className="w-10 h-10" /></button>
                        </div>}
                    </div>}
                    {getCommonQueries()}
                    <p className="text-themeBlack text-2xl font-medium">If you have any other questions, please contact our support team at <a href="mailto:support@skymd.com" className="text-themeBlack underline">support@skymd.com</a></p>
                </div>
                : <div className="flex items-center justify-start flex-col min-h-[calc(100vh-200px)] w-full">
                    {getCommonQueries()}
                    <p className="text-themeBlack text-2xl font-medium">If you have any other questions, please contact our support team at <a href="mailto:support@skymd.com" className="text-themeBlack underline">support@skymd.com</a></p>
                </div>}
            </div>
            {userDetailsFromApi?.eligible_to_create_support_ticket && 
            <div className={`fixed z-50 bottom-28 md:bottom-[44px] lg:bottom-0 right-0 w-full lg:pl-sidebar`}>
                <div
                    className={`w-full border-solid p-4 bg-white`}
                >
                    <div className="flex flex-col space-y-4">
                    {userDetailsFromApi?.any_open_support_ticket && <div className='flex items-center justify-center gap-2'><Icon name="info" className="w-6 h-6 text-gray-400" /> <p className="text-xl font-medium text-themeBlack text-center">To add a new support thread, all previous threads must be closed.</p></div>}
                    <button 
                        disabled={userDetailsFromApi?.any_open_support_ticket|| isFetchingUser}
                        onClick={() => {
                            if (userDetailsFromApi?.eligible_to_create_support_ticket) {
                                setShowModal({
                                    state: true,
                                    children: (_, onClose) => (
                                        <CreateTicketModal
                                            onClose={onClose}
                                            onSuccess={() => {
                                                refreshTickets(pageNumber);
                                            }}
                                        />
                                    ),
                                });
                            } else {
                                alert("Please contact support@skymd.com for assistance");
                            }
                        }} 
                        loading={isFetchingUser} 
                        className="w-full px-6 py-6 font-bold disabled:opacity-60 disabled:cursor-not-allowed space-x-4 bg-orange text-white rounded-lg flex items-center justify-center"
                    >
                        <FaPlus className="w-8 h-8" />
                        <span className="text-3xl font-bold">Create a Support Ticket</span>
                    </button>
                    </div>
                </div>
            </div>}
        </div>
    )
}

export default Support